/**
 * Build step 9 — Capture (voice and text) with the held list. ADR-001 §8.4, §10.2, §11.11, as amended by
 * ADR-002 Part II (§14–§17: voice notes transcribed in the browser) and Part III (goal-mvdhu5fs, t-cd196043: server
 * transcription is retired — NO AUDIO REACHES THE SERVER; a voice note arrives as browser text, editable before it
 * is saved, and R1 "Transcribe my notes on the server instead" is gone).
 *
 * The flow (ADR-001 §8.4), which every shape below serves:
 *   speak (transcribed in the browser) or type → [proposing] → proposed (countdown) ⇄ editing (countdown paused)
 *     → "Confirm now": every ticked task becomes real                        (finish = confirmed)
 *     → countdown ends: confidence ≥ threshold → task (undo in the log); below → held item   (finish = timer)
 *     → Cancel: nothing is created                                            (finish = cancelled)
 *   held item → Review queue "Confirm" (a task) or "Discard" (a dropped item, reason required).
 *
 * Rules carried verbatim: escalations are NEVER raised automatically (only ticked ones), and statements that
 * are not actions go to client notes with no confidence gate. The model proposes; our code scores (the
 * formula below); a person always has either the countdown or the held list — nothing is created and forgotten.
 * A failed proposal is never a dead end: the note ends as a held item a person must read.
 *
 * Copy is the prototype's (docs/prototype.html, "CAPTURE (voice)" and the Review queue).
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Percent, Reason, Timestamp, Uuid, Version, page } from './common.js';
import { ClientRef } from './clients.js';
import { Escalation, Severity } from './requests.js';
import { DropReason, Task, TaskPriority, TaskState } from './tasks.js';
import { UserRef } from './users.js';
// ---------------------------------------------------------------- settings and limits
/** `capture.auto_threshold` (Settings): at or above it a task is created when the countdown ends. Range 60–99. */
export const CAPTURE_AUTO_THRESHOLD_DEFAULT = 85;
export const CAPTURE_AUTO_THRESHOLD_RANGE = [60, 99];
/** `capture.countdown_seconds` (Settings): the undo window before anything is added. One of these. */
export const CAPTURE_COUNTDOWN_CHOICES = [60, 120, 180];
export const CAPTURE_COUNTDOWN_DEFAULT = 120;
/**
 * The one reader of the stored capture thresholds. They live in a single settings row, key `capture`
 * (Thresholds.capture: `{ auto_threshold, countdown_seconds }`), written by PATCH /settings/thresholds.
 * Pass that row's value; a missing or out-of-range field falls back to its default, so a bad row never stops capture.
 */
export function captureThresholdsFrom(group) {
    const g = group && typeof group === 'object' ? group : {};
    const t = g.auto_threshold;
    const c = g.countdown_seconds;
    const [lo, hi] = CAPTURE_AUTO_THRESHOLD_RANGE;
    return {
        auto_threshold: typeof t === 'number' && Number.isFinite(t) ? Math.min(hi, Math.max(lo, Math.round(t))) : CAPTURE_AUTO_THRESHOLD_DEFAULT,
        countdown_seconds: CAPTURE_COUNTDOWN_CHOICES.includes(c) ? c : CAPTURE_COUNTDOWN_DEFAULT,
    };
}
/**
 * "Timer paused while you edit" (prototype). A pause lasts at most this long; then the countdown resumes on its
 * own, so a closed tab can never leave items waiting with no timer and no held list.
 */
export const CAPTURE_EDIT_PAUSE_MAX_SECONDS = 600;
/** "Undo all" on the finished note: open to the author for this long after it finished. Later, undo per task, with a reason. */
export const CAPTURE_UNDO_ALL_MINUTES = 15;
/** A held item is late (Review queue flag, manager exception) after this many hours. Same as ReviewOther. */
export const CAPTURE_HELD_LATE_HOURS = 4;
/** A held item confirmed without a date is due this many days later (prototype `approveHeld`: due 2). */
export const CAPTURE_HELD_DEFAULT_DUE_DAYS = 2;
/** A proposed task with no date heard is due this many days later (prototype `parseNote`: due 3, "No date"). */
export const CAPTURE_NO_DATE_DUE_DAYS = 3;
/** Typed or transcribed note text. */
export const CAPTURE_TEXT_MAX = 5000;
/**
 * LEGACY, for the retired in-browser Whisper recorder (apps/web/src/lib/stt): it stopped listening after ten minutes.
 * A note dictated live uses CAPTURE_LIVE_MAX_SECONDS.
 */
export const CAPTURE_AUDIO_MAX_SECONDS = 600;
/**
 * A voice note dictated LIVE through the browser's speech service (ADR-002 §24.5) is at most 2 minutes — the same cap as
 * a live meeting recording, well inside the speech service's own 300 s cut-off. Enforced by the recorder and by
 * `audio_seconds` on POST /capture/notes.
 */
export const CAPTURE_LIVE_MAX_SECONDS = 120;
/** Per capture token (ADR-001 §10.2, §11.1). */
export const CAPTURE_TOKEN_NOTES_PER_HOUR = 30;
/**
 * Requests carrying `?t=` from one client address (an IPv4 address or an IPv6 /64) per minute, counted BEFORE the token
 * is looked up, whether it turns out valid or not (sf-f7116dfb). A token's own buckets open only once it resolves, so
 * random tokens cost the caller this budget and nothing else. Room for a whole office behind one address.
 */
export const CAPTURE_TOKEN_PER_ADDRESS_PER_MINUTE = 60;
/**
 * Every route a capture token reaches, and its per-token hourly cap. A token has no session, so the per-session
 * limits never apply to it: each route counts its own calls per token per hour instead. Notes are counted in the
 * database (they hold across API instances); the page calls in each API instance.
 */
export const CAPTURE_TOKEN_HOURLY = {
    'capture.createNote': CAPTURE_TOKEN_NOTES_PER_HOUR,
    'capture.linkInfo': CAPTURE_TOKEN_NOTES_PER_HOUR,
};
/**
 * confidence = clamp(48 + client 18 + assignee 18 + due 10 − vague 18 + 4, 30, 97); +10 (cap 97) when a person
 * picked the assignee. Pure: the worker, the PATCH handler and the tests all call this, so the threshold means
 * the same thing everywhere. Returns the number with its (i) reason lines (ADR §11.1: derived values explain).
 */
export function scoreCapture(s) {
    const explain = [
        { signal: 'base', penalty: 52, detail: 'Starting score for a proposed task', band: 'ok' },
        s.client_resolved
            ? { signal: 'client_resolved', penalty: 18, detail: 'Client recognised', band: 'ok' }
            : { signal: 'client_unresolved', penalty: 0, detail: 'No client recognised', band: 'warn' },
        s.assignee_resolved
            ? { signal: 'assignee_resolved', penalty: 18, detail: 'Person recognised', band: 'ok' }
            : { signal: 'assignee_unresolved', penalty: 0, detail: 'No person recognised', band: 'warn' },
        s.due_parsed
            ? { signal: 'due_parsed', penalty: 10, detail: 'Due date heard', band: 'ok' }
            : { signal: 'due_missing', penalty: 0, detail: 'No due date heard', band: 'warn' },
    ];
    if (s.vague)
        explain.push({ signal: 'vague', penalty: -18, detail: 'Vague wording, such as "maybe" or "look into"', band: 'bad' });
    let value = Math.max(30, Math.min(97, explain.reduce((n, r) => n + (r.penalty ?? 0), 0)));
    if (s.assignee_set_by_person) {
        explain.push({ signal: 'assignee_by_person', penalty: 10, detail: 'You picked the person', band: 'ok' });
        value = Math.min(97, value + 10);
    }
    return { value, explain };
}
/** Why a task item would be held — the prototype's words, e.g. "no client, no assignee, no date". */
export function whyHeld(s) {
    const why = [];
    if (!s.client_resolved)
        why.push('no client');
    if (!s.assignee_resolved)
        why.push('no assignee');
    if (!s.due_parsed)
        why.push('no date');
    if (s.vague)
        why.push('vague wording');
    return why.length ? why.join(', ') : 'Low confidence';
}
// ---------------------------------------------------------------- enums
/** `app` = the in-app Capture button; `link` = the personal capture link (ops.amplispot.com/c/…). */
export const CaptureSource = z.enum(['app', 'link']).meta({ id: 'CaptureSource' });
/**
 * Where the note's text came from (ADR-002 §14.3): typed, or transcribed in the browser. `server` is LEGACY: notes
 * transcribed on our own server before it was retired (t-cd196043) still read back with it; no new note has it.
 */
export const TextOrigin = z.enum(['typed', 'browser', 'server']).meta({ id: 'TextOrigin' });
/**
 * How a `browser` note's text was made (ADR-002 §24.5). `live` = dictated through the browser's standard speech service
 * (`apps/web/src/lib/speech/session.ts`), the text appearing as the member speaks, then read and edited before saving.
 * The only value. A legacy `browser` note (the retired in-browser Whisper, with a device tier) has none.
 */
export const CaptureTranscriptSource = z.enum(['live']).meta({ id: 'CaptureTranscriptSource' });
/**
 * ADR-002 §14.1 device tiers of the RETIRED in-browser Whisper (F transcribed nothing: the member typed the note).
 * Read-only now: legacy `browser` notes and held items still carry one, and the held backlog is still sliced by it.
 * No new note gets a tier (t-47e08daa).
 */
export const BrowserSttTier = z.enum(['A', 'B', 'C', 'D', 'E']).meta({ id: 'BrowserSttTier' });
/**
 * `proposing` — the language model is reading the text (worker, retried); the countdown has not started.
 * `proposed` — items shown, countdown running to `settle_at`.
 * `editing` — the author paused the countdown to edit (at most CAPTURE_EDIT_PAUSE_MAX_SECONDS).
 * `done` — confirmed, or the countdown ended. `cancelled` — the author cancelled; nothing was created.
 */
export const CaptureNoteState = z
    .enum(['proposing', 'proposed', 'editing', 'done', 'cancelled'])
    .meta({ id: 'CaptureNoteState' });
/** How a finished note finished. */
export const CaptureFinish = z.enum(['confirmed', 'timer', 'cancelled']).meta({ id: 'CaptureFinish' });
/** What the model proposed a sentence is: something to do, a possible escalation, or a statement for client notes. */
export const CaptureItemKind = z.enum(['task', 'escalation', 'statement']).meta({ id: 'CaptureItemKind' });
/**
 * `pending` until the note finishes. Tasks: `auto_created` (countdown, confidence ≥ threshold), `confirmed`
 * ("Confirm now"), `held`, `undone`. Escalations: `raised` (ticked) or `not_raised`. Statements: `noted`
 * (written to client notes) or `kept_in_note` (no client recognised — the sentence stays in the transcript).
 */
export const CaptureItemOutcome = z
    .enum(['pending', 'auto_created', 'confirmed', 'held', 'undone', 'raised', 'not_raised', 'noted', 'kept_in_note'])
    .meta({ id: 'CaptureItemOutcome' });
// ---------------------------------------------------------------- POST /capture/notes
/**
 * Text only: typed, or transcribed in the browser and checked by the member before saving → `proposing`. No audio
 * is ever sent: a recording never leaves the browser (t-cd196043). `source` must match how the caller came in
 * (`link` only with a capture token, `app` only with a session).
 *
 * The two shapes (ADR-002 §24.5, t-d8a58e6c):
 *  - typed: `{ text, text_origin: 'typed', source }`.
 *  - dictated live: `{ text, text_origin: 'browser', transcript_source: 'live', audio_seconds?, source }` — `text` is
 *    the transcript as the member edited it; `audio_seconds` is how long they spoke (at most CAPTURE_LIVE_MAX_SECONDS).
 * Nothing else: the in-browser Whisper shape (`browser_stt`) was retired with apps/web/src/lib/stt (t-47e08daa) and is
 * now an unknown key → 422. Notes it made earlier are still read as they were stored (tier, model, revision).
 */
export const CreateCaptureNoteBody = z
    .object({
    text: z
        .string()
        .trim()
        .min(3, 'Say or type a little more')
        .max(CAPTURE_TEXT_MAX, 'Keep a note under 5,000 characters. Split a long one into two.'),
    text_origin: TextOrigin.exclude(['server']),
    /** `live` for a note dictated through the browser's speech service; `text_origin` must then be `browser`. */
    transcript_source: CaptureTranscriptSource.optional(),
    /** A live note only: how long the member spoke, in seconds (at most 2 minutes). */
    audio_seconds: z
        .number()
        .nonnegative()
        .max(CAPTURE_LIVE_MAX_SECONDS, 'A voice note is at most 2 minutes')
        .optional(),
    source: CaptureSource,
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.text_origin === 'browser') {
        if (!b.transcript_source) {
            ctx.addIssue({ code: 'custom', path: ['transcript_source'], message: 'Say how the browser made the text: "live"' });
        }
    }
    else if (b.transcript_source) {
        ctx.addIssue({ code: 'custom', path: ['transcript_source'], message: 'Send transcript_source only for text transcribed in the browser' });
    }
    if (b.audio_seconds !== undefined && !b.transcript_source) {
        ctx.addIssue({ code: 'custom', path: ['audio_seconds'], message: 'Only a note dictated live carries audio_seconds' });
    }
});
/** `202` — accepted. The same for a capture-link caller, who can read nothing back (ADR-001 §10.2). */
export const CaptureNoteAccepted = z
    .object({
    note_id: Uuid,
    state: z.enum(['proposing', 'proposed']),
    /** When the countdown ends; `null` until items are proposed. */
    settle_at: Timestamp.nullable(),
})
    .meta({ id: 'CaptureNoteAccepted' });
// ---------------------------------------------------------------- GET /capture/notes/{id}
/** A transcript, stored verbatim (ADR-002 §14.5) — a person can always read what was actually said. */
export const CaptureTranscript = z
    .object({
    text: z.string(),
    origin: TextOrigin,
    /** `live` for a note dictated through the browser's speech service; `null` for typed text and legacy notes. */
    transcript_source: CaptureTranscriptSource.nullable(),
    /** `null` for typed text and for a note dictated live (no model of ours made it). */
    stt_model: z.string().nullable(),
    stt_model_revision: z.string().nullable(),
    tier: BrowserSttTier.nullable(),
    at: Timestamp,
})
    .meta({ id: 'CaptureTranscript' });
export const CaptureItem = z
    .object({
    id: Uuid,
    kind: CaptureItemKind,
    /** The proposed task title, or the sentence for an escalation or a statement. */
    title: z.string(),
    /** `null` = "No client", or a client the caller cannot see (the words heard stay in `client_guess`). */
    client: ClientRef.nullable(),
    /** The client name as heard, e.g. "Meridian". */
    client_guess: z.string().nullable(),
    /** Tasks only. Defaults to the client's SPOC when no person was heard. */
    assignee: UserRef.nullable(),
    /** Tasks only; `null` = "No date" (the task is then due CAPTURE_NO_DATE_DUE_DAYS later). */
    due: IsoDate.nullable(),
    /** Tasks only (prototype: "urgent"/"today" → high, else medium). */
    priority: TaskPriority.nullable(),
    /** Escalations only: `high` or `med` as proposed. */
    severity: Severity.nullable(),
    /** Escalations only: ticked to raise. Never set by the system — escalations are never raised on their own. */
    raise: z.boolean(),
    /** 30–97, from `scoreCapture`. */
    confidence: z.number().int().min(30).max(97),
    confidence_explain: z.array(Reason),
    /** The language model's flag; the arithmetic stays in our code. */
    vagueness: z.boolean(),
    /** Tasks below the threshold only, e.g. "no client, no date". */
    why_held: z.string().nullable(),
    /** Tasks only, while pending: what the countdown will do — "Auto-approve" or "Held". */
    will: z.enum(['auto_create', 'hold']).nullable(),
    outcome: CaptureItemOutcome,
    task_id: Uuid.nullable(),
    held_item_id: Uuid.nullable(),
    escalation_id: Uuid.nullable(),
    client_note_id: Uuid.nullable(),
    /** A person changed this item while checking the note. */
    edited: z.boolean(),
})
    .meta({ id: 'CaptureItem' });
/** The prototype's "Added to Ops." line: confirmed, auto-approved, held, statements saved, escalations raised. */
export const CaptureSummary = z
    .object({
    confirmed: z.number().int().nonnegative(),
    auto_created: z.number().int().nonnegative(),
    held: z.number().int().nonnegative(),
    statements_noted: z.number().int().nonnegative(),
    escalations_raised: z.number().int().nonnegative(),
})
    .meta({ id: 'CaptureSummary' });
export const CaptureNoteHeader = z
    .object({
    id: Uuid,
    source: CaptureSource,
    by: UserRef,
    created_at: Timestamp,
    /** Where the text the items were proposed from came from; `null` only for a legacy note never transcribed. */
    text_origin: TextOrigin.nullable(),
    /** The threshold and countdown this note runs under (Settings at the time it was proposed). */
    auto_threshold: z.number().int().min(60).max(99),
    countdown_seconds: z.number().int().positive(),
    /** Seconds left on a paused countdown; `null` unless `editing`. */
    paused_remaining_seconds: z.number().int().nonnegative().nullable(),
    /** When a pause ends on its own; `null` unless `editing`. */
    pause_ends_at: Timestamp.nullable(),
    finish: CaptureFinish.nullable(),
    finished_at: Timestamp.nullable(),
    /** "Undo all" is open until then; `null` when it is not (not finished, cancelled, or already undone). */
    undo_all_until: Timestamp.nullable(),
    summary: CaptureSummary.nullable(),
    version: Version,
})
    .meta({ id: 'CaptureNoteHeader' });
export const CaptureNote = z
    .object({
    note: CaptureNoteHeader,
    /** What the items were proposed from; `null` only for a legacy note never transcribed. */
    transcript: CaptureTranscript.nullable(),
    items: z.array(CaptureItem),
    settle_at: Timestamp.nullable(),
    state: CaptureNoteState,
})
    .meta({ id: 'CaptureNote' });
export const CaptureNoteIdParams = z.object({ id: Uuid });
export const CaptureItemParams = z.object({ id: Uuid, iid: Uuid });
// ---------------------------------------------------------------- GET /capture/notes
export const CaptureNoteSummaryRow = z
    .object({
    id: Uuid,
    state: CaptureNoteState,
    source: CaptureSource,
    by: UserRef,
    created_at: Timestamp,
    /** The first 140 characters of the text; `null` only for a legacy note never transcribed. */
    preview: z.string().nullable(),
    settle_at: Timestamp.nullable(),
    summary: CaptureSummary.nullable(),
})
    .meta({ id: 'CaptureNoteSummaryRow' });
export const CaptureNotePage = page(CaptureNoteSummaryRow).meta({ id: 'CaptureNotePage' });
const csvStates = z
    .string()
    .max(200)
    .transform((s, ctx) => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const bad = parts.filter((p) => !CaptureNoteState.safeParse(p).success);
    if (bad.length) {
        ctx.addIssue({ code: 'custom', message: `Unknown state(s): ${bad.join(', ')}. Use: ${CaptureNoteState.options.join(', ')}` });
        return z.NEVER;
    }
    return parts;
});
export const ListCaptureNotesQuery = PageQuery.extend({
    /** Comma list; default all. */
    state: csvStates.optional(),
    /** Managers only; others always see their own notes (another id is ignored). */
    by_id: Uuid.optional(),
});
// ---------------------------------------------------------------- PATCH /capture/notes/{id}/items/{iid}
/** The author edits an item while checking the note. `null` clears ("No client", "No date"). */
export const UpdateCaptureItemBody = z
    .object({
    title: z.string().trim().min(3, 'Say what needs doing in a few words').max(200).optional(),
    client_id: Uuid.nullable().optional(),
    assignee_id: Uuid.optional(),
    due: IsoDate.nullable().optional(),
    kind: CaptureItemKind.optional(),
    priority: TaskPriority.optional(),
    severity: Severity.optional(),
    /** Escalations only: tick or untick "raise". */
    raise: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
/** The item with its recomputed confidence, and the note's new version for the next If-Match. */
export const UpdateCaptureItemResponse = z
    .object({ item: CaptureItem, note_version: Version })
    .meta({ id: 'UpdateCaptureItemResponse' });
// ---------------------------------------------------------------- POST /capture/notes/{id}/confirm
/**
 * "Confirm now". `item_ids`: the task items to create now (the UI sends every task item). A task item NOT listed
 * is held, never lost. `escalation_item_ids`: the ticked escalations; every other escalation item is not raised.
 */
export const ConfirmCaptureBody = z
    .object({
    item_ids: z.array(Uuid).max(100),
    escalation_item_ids: z.array(Uuid).max(100),
})
    .strict()
    .superRefine((b, ctx) => {
    if (new Set(b.item_ids).size !== b.item_ids.length)
        ctx.addIssue({ code: 'custom', path: ['item_ids'], message: 'List each item once' });
    if (new Set(b.escalation_item_ids).size !== b.escalation_item_ids.length) {
        ctx.addIssue({ code: 'custom', path: ['escalation_item_ids'], message: 'List each item once' });
    }
    if (b.item_ids.some((id) => b.escalation_item_ids.includes(id))) {
        ctx.addIssue({ code: 'custom', path: ['escalation_item_ids'], message: 'An item is either a task or an escalation, not both' });
    }
});
// ---------------------------------------------------------------- held items
export const HeldStatus = z.enum(['open', 'confirmed', 'discarded']).meta({ id: 'HeldStatus' });
/** Guide §4 "Held item": id, text, client guess, confidence, why held, by, when. */
export const HeldItem = z
    .object({
    id: Uuid,
    note_id: Uuid,
    item_id: Uuid,
    raw_text: z.string(),
    /** The client name as heard. */
    client_guess: z.string().nullable(),
    /** The client it resolved to, when the caller can see it. */
    client: ClientRef.nullable(),
    /** The person proposed (the Review queue's pre-selected assignee). */
    suggested_assignee: UserRef.nullable(),
    due: IsoDate.nullable(),
    confidence: z.number().int().min(30).max(97),
    confidence_explain: z.array(Reason),
    why_held: z.string(),
    /** Who captured it. */
    by: UserRef,
    at: Timestamp,
    age_hours: z.number().nonnegative(),
    /** Open for more than CAPTURE_HELD_LATE_HOURS. */
    is_late: z.boolean(),
    /** Where the text came from — the backlog split that tells transcription from resolution (ADR-002 §17.2). */
    text_origin: TextOrigin,
    status: HeldStatus,
    task_id: Uuid.nullable(),
    dropped_item_id: Uuid.nullable(),
    resolved_by: UserRef.nullable(),
    resolved_at: Timestamp.nullable(),
    version: Version,
})
    .meta({ id: 'HeldItem' });
export const HeldPage = page(HeldItem).meta({ id: 'HeldPage' });
export const HeldIdParams = z.object({ id: Uuid });
/** What "Confirm now" made. Statements are written to client notes in the same transaction. */
export const ConfirmCaptureResponse = z
    .object({
    created_tasks: z.array(Task),
    created_escalations: z.array(Escalation),
    held: z.array(HeldItem),
    statements_noted: z.number().int().nonnegative(),
    note: CaptureNote,
})
    .meta({ id: 'ConfirmCaptureResponse' });
const BoolString = z.enum(['true', 'false']).transform((s) => s === 'true');
export const ListHeldQuery = PageQuery.extend({
    /** Default `open`. */
    status: z.enum(['open', 'confirmed', 'discarded', 'all']).optional(),
    /** Managers only; ignored for others, who see what they captured. */
    by_id: Uuid.optional(),
    /** `true` = only items past CAPTURE_HELD_LATE_HOURS. */
    late: BoolString.optional(),
});
/** Review queue "Confirm": the person is required (the reason most items were held). */
export const ConfirmHeldBody = z
    .object({
    assignee_id: Uuid,
    client_id: Uuid.nullable().optional(),
    title: z.string().trim().min(3, 'Say what needs doing in a few words').max(200).optional(),
    due: IsoDate.optional(),
    priority: TaskPriority.optional(),
})
    .strict();
const Note = z.string().trim().min(1).max(1000);
const needsNoteForOther = (b, ctx) => {
    if (b.reason_code === 'other' && !b.note) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say what the reason is when you pick Other' });
    }
};
/** Review queue "Discard" and auto-log "Undo": a reason is required (no path to dropped without one). */
export const DiscardHeldBody = z.object({ reason_code: DropReason, note: Note.optional() }).strict().superRefine(needsNoteForOther);
export const UndoAutoCreatedBody = z.object({ reason_code: DropReason, note: Note.optional() }).strict().superRefine(needsNoteForOther);
/**
 * The held-item backlog — the live quality signal for transcription (ADR-002 §17.2–17.3: "must be live on the
 * dashboard from day one of build step 9"). Split by where the text came from and the browser tier, so a
 * rising backlog says whether transcription or resolution is the cause.
 */
export const HeldBacklogSlice = z
    .object({
    origin: TextOrigin,
    /**
     * The legacy in-browser Whisper tier. `browser` with `null` = notes dictated LIVE through the browser's speech
     * service (ADR-002 §24.5: there is no tier); a legacy `browser` note always has one.
     */
    tier: BrowserSttTier.nullable(),
    task_items_7d: z.number().int().nonnegative(),
    held_7d: z.number().int().nonnegative(),
    /** `null` when there were no task items. */
    held_rate_pct: Percent.nullable(),
})
    .meta({ id: 'HeldBacklogSlice' });
export const HeldBacklog = z
    .object({
    as_of: Timestamp,
    open: z.number().int().nonnegative(),
    late: z.number().int().nonnegative(),
    oldest_age_hours: z.number().nonnegative().nullable(),
    created_today: z.number().int().nonnegative(),
    resolved_today: z.number().int().nonnegative(),
    /** Held ÷ task items over the last 7 days; `null` when there were none. */
    held_rate_7d_pct: Percent.nullable(),
    by_origin: z.array(HeldBacklogSlice),
})
    .meta({ id: 'HeldBacklog' });
// ---------------------------------------------------------------- auto-created log
/** Review queue → "Auto-approved log", and the Capture screen's "Your recent captures". */
export const AutoCreated = z
    .object({
    task_id: Uuid,
    title: z.string(),
    client: ClientRef.nullable(),
    assignee: UserRef.nullable(),
    confidence: z.number().int().min(30).max(97),
    /** Who captured it. */
    by: UserRef,
    note_id: Uuid,
    item_id: Uuid,
    created_at: Timestamp,
    task_state: TaskState,
    undone: z.boolean(),
    undone_by: UserRef.nullable(),
    undone_at: Timestamp.nullable(),
    dropped_item_id: Uuid.nullable(),
})
    .meta({ id: 'AutoCreated' });
export const AutoCreatedPage = page(AutoCreated).meta({ id: 'AutoCreatedPage' });
export const ListAutoCreatedQuery = PageQuery.extend({
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    /** Managers only; ignored for others, who see what they captured. */
    by_id: Uuid.optional(),
    /** Default `true`. */
    include_undone: BoolString.optional(),
}).superRefine((q, ctx) => {
    if (q.from && q.to && q.from > q.to)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Pick an end date on or after the start date' });
});
export const AutoCreatedTaskParams = z.object({ task_id: Uuid });
/** POST /capture/notes/{id}/undo — "Undo all" right after the note finished. */
export const UndoCaptureNoteResponse = z
    .object({
    removed_task_ids: z.array(Uuid),
    removed_held_item_ids: z.array(Uuid),
    /** Raised escalations and client-note statements stay (prototype "Undo all" removes tasks only). */
    kept_escalation_ids: z.array(Uuid),
})
    .meta({ id: 'UndoCaptureNoteResponse' });
// ---------------------------------------------------------------- my capture settings, and the link page
/** The member's capture settings and link status (Capture screen header, Settings). */
export const MyCaptureSettings = z
    .object({
    /** `stt.note_language` (ADR-002 §16): the browser recogniser's language — Roman-script Hinglish via `en`. */
    note_language: z.string(),
    auto_threshold: z.number().int().min(60).max(99),
    countdown_seconds: z.number().int().positive(),
    /** The personal link exists. Its URL is shown once, on rotate (POST /users/{id}/capture-token/rotate). */
    capture_link_active: z.boolean(),
    capture_link_issued_at: Timestamp.nullable(),
})
    .meta({ id: 'MyCaptureSettings' });
/**
 * `?t=<capture token>` — the only way a link caller authenticates (ADR-001 §11.1). Never logged (Pino redaction).
 * 20 random bytes, base64url.
 */
export const CaptureTokenQuery = z.object({
    t: z.string().regex(/^[A-Za-z0-9_-]{20,64}$/, 'This capture link is not valid. Ask for a new one in Settings.'),
});
/** What the link page may know: a greeting and the recogniser's language. Nothing else. */
export const CaptureLinkInfo = z
    .object({
    first_name: z.string(),
    note_language: z.string(),
})
    .meta({ id: 'CaptureLinkInfo' });
//# sourceMappingURL=capture.js.map